/**
 * Use Debounce Hook
 *
 * Custom hook for debouncing values to prevent excessive API calls.
 *
 * @since 1.0.0
 */

import { useEffect, useState } from 'react';

import type { UseDebounceParams, UseDebounceReturn } from './types';

/**
 * Custom hook for debouncing values
 *
 * @since 1.0.0
 *
 * @param params Hook parameters.
 *
 * @return Debounced value.
 */
export const useDebounce = <T>(params: UseDebounceParams<T>): UseDebounceReturn<T> => {
  const { value, delay = 300, minLength = 0 } = params;
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    // If value is a string and doesn't meet minimum length, don't debounce
    if (typeof value === 'string' && value.length > 0 && value.length < minLength) {
      // Reset to empty string or initial value if below minimum length
      setDebouncedValue('' as T);
      return;
    }

    // Set up the debounce timer
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Cleanup timer on value change or component unmount
    return () => {
      clearTimeout(timer);
    };
  }, [value, delay, minLength]);

  return {
    debouncedValue,
  };
};
